import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DonationHistoryTable = ({ donations }) => {
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const sortedDonations = [...donations]?.sort((a, b) => {
    let aValue = a?.[sortBy];
    let bValue = b?.[sortBy];
    
    if (sortBy === 'date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    } else if (sortBy === 'amount') {
      aValue = parseFloat(aValue);
      bValue = parseFloat(bValue);
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleDownloadReceipt = (donationId) => {
    console.log('Downloading receipt for donation:', donationId);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <h3 className="text-lg font-semibold text-card-foreground">Donation History</h3>
        <p className="text-sm text-muted-foreground">Track your charitable contributions</p>
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('charity')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Charity</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('amount')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Amount</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center space-x-1 text-sm font-medium text-muted-foreground hover:text-foreground"
                >
                  <span>Date</span>
                  <Icon name="ArrowUpDown" size={14} />
                </button>
              </th>
              <th className="px-6 py-3 text-left">
                <span className="text-sm font-medium text-muted-foreground">Status</span>
              </th>
              <th className="px-6 py-3 text-left">
                <span className="text-sm font-medium text-muted-foreground">Receipt</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedDonations?.map((donation) => (
              <tr key={donation?.id} className="hover:bg-muted/30">
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Heart" size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-card-foreground">{donation?.charity}</p>
                      <p className="text-sm text-muted-foreground">{donation?.category}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="font-mono font-medium text-card-foreground">${donation?.amount}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-muted-foreground">{formatDate(donation?.date)}</span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    donation?.status === 'completed' 
                      ? 'bg-success/10 text-success' 
                      : donation?.status === 'pending' ?'bg-warning/10 text-warning' :'bg-error/10 text-error'
                  }`}>
                    {donation?.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDownloadReceipt(donation?.id)}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Receipt
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-border">
        {sortedDonations?.map((donation) => (
          <div key={donation?.id} className="p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Heart" size={16} className="text-primary" />
                </div>
                <div>
                  <p className="font-medium text-card-foreground">{donation?.charity}</p>
                  <p className="text-sm text-muted-foreground">{donation?.category}</p>
                </div>
              </div>
              <span className="font-mono font-medium text-card-foreground">${donation?.amount}</span>
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-4">
                <span className="text-muted-foreground">{formatDate(donation?.date)}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  donation?.status === 'completed' 
                    ? 'bg-success/10 text-success' 
                    : donation?.status === 'pending' ?'bg-warning/10 text-warning' :'bg-error/10 text-error'
                }`}>
                  {donation?.status}
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDownloadReceipt(donation?.id)}
                iconName="Download"
              >
                Receipt
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DonationHistoryTable;